import { ValueListPlain, BatchOptionUsage, CreateValueListPayload, UpdateValueListPayload } from '../types/valueList';
import { g17ecoApi, transformResponse } from './g17ecoApi';

const valueListTag = 'value-list';
const valueListIdsTag = 'value-list-ids';
const valueListsTag = 'value-lists';

export const valueListApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: [valueListTag, valueListIdsTag, valueListsTag],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getValueListById: builder.query<ValueListPlain, string>({
        transformResponse,
        query: (listId) => {
          return {
            url: `value-list/${listId}`,
            method: 'get',
          };
        },
        providesTags: (_result, _error, listId) => [{ type: valueListTag, id: listId }],
      }),
      getValueListByIds: builder.query<ValueListPlain[], string[]>({
        transformResponse,
        query: (ids) => {
          return {
            url: 'value-list/ids',
            method: 'post',
            data: {
              ids,
            },
          };
        },
        providesTags: (result, error, ids) => ids.map((id) => ({ type: valueListIdsTag, id })),
      }),
      getSharableValueListById: builder.query<
        ValueListPlain,
        { valueListId: string; dashboardId: string; token: string }
      >({
        transformResponse,
        query: ({ valueListId, dashboardId, token }) => {
          return {
            url: `o/value-list/${valueListId}/insight-dashboards/${dashboardId}/token/${token}`,
            method: 'get',
          };
        },
        providesTags: (_result, _error, { valueListId }) => [{ type: valueListTag, id: valueListId }],
      }),
      // Get all value lists by ownerId
      getValueLists: builder.query<ValueListPlain[], { ownerId: string }>({
        transformResponse,
        query: ({ ownerId }) => ({
          url: `value-list/initiativeId/${ownerId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, { ownerId }) => [{ type: valueListsTag, id: ownerId }],
      }),
      // Check usage for multiple options in a batch
      checkBatchValueListOptionUsage: builder.query<BatchOptionUsage, { listId: string; optionCodes: string[] }>({
        transformResponse,
        query: ({ listId, optionCodes }) => ({
          url: `value-list/${listId}/options/batch-usage`,
          method: 'post',
          data: { optionCodes },
        }),
      }),
      // Create a new value list
      createValueList: builder.mutation<ValueListPlain, CreateValueListPayload>({
        transformResponse,
        query: (data) => ({
          url: 'value-list',
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { ownerId }) => [{ type: valueListsTag, id: ownerId }],
      }),
      // Update an existing value list
      updateValueList: builder.mutation<ValueListPlain, UpdateValueListPayload & { id: string }>({
        transformResponse,
        query: ({ id, ...data }) => ({
          url: `value-list/${id}`,
          method: 'put',
          data,
        }),
        invalidatesTags: (_result, _error, { ownerId, id }) => [
          { type: valueListsTag, id: ownerId },
          { type: valueListTag, id },
        ],
      }),
    }),
  });

export const {
  useGetValueListByIdQuery,
  useGetSharableValueListByIdQuery,
  useGetValueListByIdsQuery,
  useGetValueListsQuery,
  useCheckBatchValueListOptionUsageQuery,
  useCreateValueListMutation,
  useUpdateValueListMutation,
} = valueListApi;
